// §18 — manager dispute override dialog. Only valid while DISPUTED. Mirrors the
// structure of the transit-exception resolve modal: context + radio-card actions
// + required note + per-action side-effects, submitting to escalateDispute.

import {
  Alert, Badge, Box, Button, Card, Divider, Grid, Group, Modal, Skeleton,
  Stack, Text, Textarea, Title
} from "@mantine/core";
import { showNotification } from "@dehub/theme";
import { WarningIcon } from "@phosphor-icons/react";
import { useEffect, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnApi } from "../../apis";
import { currencyFormat } from "../../utils";
import { dateTimeSecFormat } from "../../utils/date";
import type { DisputeOverrideAction, IReturnDetail } from "../../apis/return";
import { TrustBadge } from "./trust-badge";

type TFn = (k: string, o?: Record<string, unknown>) => string;

const ACTION_COLOR: Record<DisputeOverrideAction, string> = {
  RESET_DISPUTE  : "blue",
  FORCE_REJECTED : "red",
  FORCE_ACCEPTED : "green",
  ESCALATE_SENIOR: "grape"
};

const actionCards = (t: TFn): { value: DisputeOverrideAction; title: string; desc: string; color: string }[] =>
  (Object.keys(ACTION_COLOR) as DisputeOverrideAction[]).map((value) => ({
    value,
    title: t(`disputeOverride.action.${value}.title`),
    desc: t(`disputeOverride.action.${value}.desc`),
    color: ACTION_COLOR[value]
  }));

const sideEffect = (t: TFn, action: DisputeOverrideAction): { color: string; text: string } => ({
  color: ACTION_COLOR[action],
  text: t(`disputeOverride.sideEffect.${action}`)
});

const fmt = (s?: string | null) => dateTimeSecFormat(s);

interface Props {
  detail: IReturnDetail | null;
  opened: boolean;
  onClose: () => void;
  onResolved: () => void;
}

export function DisputeOverrideModal ({ detail, opened, onClose, onResolved }: Props){
  const { t } = useTranslation("returns");
  const [action, setAction] = useState<DisputeOverrideAction | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Dispute history comes from the timeline (not on IReturnDetail) — fetch it.
  const { data: full, isLoading } = useSWR(
    opened && detail ? ["returns.get", detail.id] : null,
    () => ReturnApi.get(detail!.id),
    { revalidateOnFocus: false }
  );
  const disputeEntries = (full?.timeline ?? []).filter((t) => t.action === "DISPUTED");

  useEffect(() => {
    if (opened) { setAction(null); setNote(""); setShowErrors(false); }
  }, [opened, detail?.id]);

  if (!detail) return null;

  const noteOk = note.trim().length >= 3;
  const valid = !!action && noteOk;
  const total = detail.totalRequestedAmount ?? 0;

  const submit = async () => {
    setShowErrors(true);
    if (!action) { showNotification({ color: "red", title: t("toast.error"), message: t("disputeOverride.pickAction") }); return; }
    if (!noteOk) { showNotification({ color: "red", title: t("toast.error"), message: t("disputeOverride.noteMin") }); return; }
    try {
      setBusy(true);
      await ReturnApi.escalateDispute(detail.id, { action, note: note.trim(), expectedVersion: detail.version });
      showNotification({ color: "green", title: t("toast.success"), message: t("disputeOverride.done") });
      onResolved();
      onClose();
    } catch (e) {
      showNotification({ color: "red", title: t("toast.error"), message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t("disputeOverride.title", { number: detail.returnNumber })} size={600}>
      <Stack gap="md">
        {/* Context */}
        <Card withBorder radius="md" padding="sm">
          <Group justify="space-between" mb="xs">
            <Title order={6}>{t("disputeOverride.history")}</Title>
            <Group gap="xs">
              <Badge color="gray" variant="light" size="lg" radius="xl">{t("disputeOverride.disputeCount", { count: detail.disputeCount })}</Badge>
              {detail.buyerBusinessId ? <TrustBadge businessId={detail.buyerBusinessId} size="sm" /> : null}
            </Group>
          </Group>
          {isLoading ? (
            <Skeleton h={60} />
          ) : disputeEntries.length ? (
            <Stack gap="xs">
              {disputeEntries.map((e, i) => (
                <Box key={`${e.timestamp}-${i}`}>
                  <Text size="sm" c="dimmed">{t("disputeOverride.disputeN", { number: i + 1 })} · {fmt(e.timestamp)}</Text>
                  {e.notes ? <Text size="sm">{e.notes}</Text> : null}
                </Box>
              ))}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">{t("disputeOverride.noNotes")}</Text>
          )}
        </Card>

        {/* Action choice */}
        <Box>
          <Text size="sm" fw={600} mb={6}>{t("disputeOverride.managerAction")}</Text>
          <Grid>
            {actionCards(t).map((a) => {
              const active = action === a.value;
              return (
                <Grid.Col key={a.value} span={{ base: 12, sm: 6 }}>
                  <Card
                    withBorder radius="md" padding="sm"
                    onClick={() => setAction(a.value)}
                    role="radio"
                    aria-checked={active}
                    tabIndex={0}
                    onKeyDown={(e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") setAction(a.value); }}
                    styles={{ root: {
                      cursor: "pointer",
                      // Selected = grey background + darker grey border (no coloured borders, canvas Ord_Rules O39).
                      borderColor: active ? "var(--mantine-color-gray-6)" : undefined,
                      background: active ? "var(--mantine-color-gray-1)" : undefined
                    } }}
                  >
                    <Text size="sm" fw={600}>{a.title}</Text>
                    <Text size="sm" c="dimmed">{a.desc}</Text>
                  </Card>
                </Grid.Col>
              );
            })}
          </Grid>
          {showErrors && !action ? <Text size="sm" c="red" mt={4}>{t("disputeOverride.pickAction")}</Text> : null}
        </Box>

        {action === "FORCE_ACCEPTED" ? (
          <Text size="sm" c="dimmed">
            {t("disputeOverride.refundAmountLabel")} <Text span fw={600}>{currencyFormat(total)}</Text> (100%)
          </Text>
        ) : null}

        <Textarea
          label={t("disputeOverride.managerNote")}
          description={t("disputeOverride.auditRequired")}
          required
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          error={showErrors && !noteOk ? t("transitResolve.min3") : undefined}
          autosize minRows={2}
        />

        {action ? (
          <Alert color="gray" variant="light" icon={<WarningIcon size={16} />} title={t("transitResolve.afterResolveTitle")}>
            <Text size="sm">{sideEffect(t, action).text}</Text>
          </Alert>
        ) : null}

        <Divider />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={busy}>{t("resolutionPanel.cancel")}</Button>
          <Button onClick={submit} loading={busy} disabled={!valid}>{t("disputeOverride.override")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
